import { Helmet } from 'react-helmet-async';

interface SEOHeadProps {
  title?: string;
  description?: string;
  keywords?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogImage?: string;
  canonical?: string;
  noindex?: boolean;
  structuredData?: object;
}

export default function SEOHead({
  title = "Dine AI - AI-Powered Revenue Engine for Restaurants",
  description = "Transform your restaurant with AI. Answer customer questions instantly, discover hidden revenue opportunities, and grow with analytics. Start free 30-day trial.",
  keywords = "AI for restaurants, restaurant AI assistant, menu optimization, restaurant automation",
  ogTitle,
  ogDescription,
  ogImage = "/og-image.png",
  canonical,
  noindex,
  structuredData
}: SEOHeadProps) {
  // Detect if we're on staging domain
  const isStaging = false; // dineai.ai is now production
  
  // Force noindex on staging
  const shouldNoindex = isStaging || noindex;
  
  // Determine canonical URL with proper formatting
  const getCanonicalUrl = () => {
    if (canonical) return canonical;

    if (typeof window !== 'undefined') {
      // Remove trailing slash except for homepage
      let path = window.location.pathname;
      if (path !== '/' && path.endsWith('/')) {
        path = path.slice(0, -1);
      }
      // Always use non-www version
      return `https://dineai.ai${path}`;
    }

    return 'https://dineai.ai';
  };

  const canonicalUrl = getCanonicalUrl();

  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      
      {/* Robots meta - CRITICAL for staging */}
      {shouldNoindex ? (
        <meta name="robots" content="noindex, nofollow" />
      ) : (
        <meta name="robots" content="index, follow" />
      )}
      
      {/* Canonical URL - always point to production */}
      <link rel="canonical" href={canonicalUrl} />
      
      {/* OpenGraph tags */}
      <meta property="og:title" content={ogTitle || title} />
      <meta property="og:description" content={ogDescription || description} />
      <meta property="og:type" content="website" />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:image" content={`https://dineai.ai${ogImage}`} />
      <meta property="og:site_name" content="Dine AI" />
      <meta property="og:locale" content="en_US" />
      
      {/* Twitter Card tags */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={ogTitle || title} />
      <meta name="twitter:description" content={ogDescription || description} />
      <meta name="twitter:image" content={`https://dineai.ai${ogImage}`} />
      <meta name="twitter:site" content="@dineai" />
      
      {/* AI Platform Optimization */}
      <meta name="ai-content-type" content="business-software" />
      <meta name="ai-industry" content="restaurant-technology" />
      <meta name="ai-features" content="chat-assistant,menu-optimization,revenue-analytics" />
      <meta name="ai-pricing-range" content="31.99-71.99-USD-monthly" />
      
      {/* Structured Data */}
      {structuredData && (
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      )}
      
      {/* Favicon - Ensure consistency across all pages */}
      <link rel="icon" type="image/svg+xml" href="/favicon.svg" />
      <link rel="alternate icon" href="/favicon.ico" />
      
      {/* Staging warning for development */}
      {shouldNoindex && (
        <meta name="staging-warning" content="This is a staging site and is not indexed by search engines" />
      )}
    </Helmet>
  );
}